import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Container,
  MenuItem,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { Features, UserRole } from "../../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../../Helpers/authHelpers";
import {
  getVoicesWorkspaceId,
  setVoicesWorkspaceId,
} from "../../../Helpers/voicesWorkspace";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import {
  useVoicesConfig,
  useVoicesWorkspaces,
} from "../../../Query/useVoicesConfig";
import { useVoicesSync, useVoicesToday } from "../../../Query/useVoicesOverview";
import VoicesInput from "../Common/VoicesInput";
import VoicesThemeProvider from "../Common/VoicesThemeProvider";
import { voicesNavigation } from "./voicesNavigation";

const TODAY_PATH = "/voices/today";
const RESULTS_PATH = "/voices/results";

const VoicesLayout: React.FC = () => {
  const { t } = useTranslation("voicesCommon");
  const { pathname } = useLocation();
  const { colors: workspace } = useWorkspaceColors();
  const queryClient = useQueryClient();

  const isAdmin = hasRole(UserRole.ADMIN);
  const isMarketing = isAdmin || hasPermission(Features.VOICES_MARKETING);

  const { data: workspaces = [], isLoading: workspacesLoading } =
    useVoicesWorkspaces(isAdmin);
  const [workspaceId, setWorkspaceId] = useState<number | null>(
    getVoicesWorkspaceId(),
  );
  const workspaceChosen =
    !isAdmin || workspaces.some((item) => item.id === workspaceId);

  useEffect(() => {
    if (!isAdmin || !workspaces.length || workspaceChosen) return;
    setVoicesWorkspaceId(workspaces[0].id);
    setWorkspaceId(workspaces[0].id);
  }, [isAdmin, workspaces, workspaceChosen]);

  const { data: config } = useVoicesConfig(isMarketing && workspaceChosen);
  const { data: today } = useVoicesToday(workspaceChosen);
  const sync = useVoicesSync(workspaceChosen);
  const startSync = sync.start.mutate;

  useEffect(() => {
    if (!workspaceChosen) return;
    startSync({ kind: pathname.startsWith(RESULTS_PATH) ? "all" : "status", manual: false });
  }, [pathname, workspaceChosen, workspaceId, startSync]);

  const switchWorkspace = (nextWorkspaceId: number) => {
    setVoicesWorkspaceId(nextWorkspaceId);
    queryClient.removeQueries({
      predicate: (query) =>
        String(query.queryKey[0]).startsWith("voices") &&
        query.queryKey[0] !== "voicesWorkspaces",
    });
    setWorkspaceId(nextWorkspaceId);
  };

  const items = voicesNavigation.filter(
    (item) => isAdmin || item.features.some(hasPermission),
  );
  const activeItem = items.find((item) => pathname.startsWith(item.path));

  const renderContent = () => {
    if (workspaceChosen) return <Outlet key={workspaceId ?? "own"} />;
    if (isAdmin && !workspacesLoading && !workspaces.length) {
      return (
        <Typography variant="body2" sx={{ color: color.gray500 }}>
          {t("noWorkspaces")}
        </Typography>
      );
    }
    return null;
  };

  return (
    <VoicesThemeProvider>
    <Box sx={{ minHeight: "100vh", backgroundColor: color.gray50 }}>
      <Box
        sx={{
          backgroundColor: color.white,
          borderBottom: `1px solid ${color.gray200}`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", pt: 3 }}>
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "1.75rem",
                color: color.gray900,
              }}
            >
              {t("title")}
            </Typography>
          </Box>

          {workspaceChosen && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                pt: 2,
                flexWrap: "wrap",
              }}
            >
              {isAdmin && workspaceChosen && (
                <VoicesInput
                  select
                  size="small"
                  label={t("workspace")}
                  value={workspaceId ?? ""}
                  onChange={(e) => switchWorkspace(Number(e.target.value))}
                  sx={{ width: 260 }}
                >
                  {workspaces.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </VoicesInput>
              )}
              {config && (
                <Chip
                  size="small"
                  label={
                    config.buffer_connected
                      ? t("bufferConnected")
                      : t("bufferNotConnected")
                  }
                  sx={{
                    backgroundColor: config.buffer_connected
                      ? color.orange50
                      : color.gray100,
                    border: `1px solid ${
                      config.buffer_connected ? color.orange200 : color.gray200
                    }`,
                    color: color.gray900,
                  }}
                />
              )}
            </Box>
          )}

          <Tabs
            value={activeItem ? activeItem.path : false}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.9rem",
                color: color.gray500,
                minHeight: 56,
              },
              "& .Mui-selected": { color: `${workspace.accent} !important` },
              "& .MuiTabs-indicator": {
                backgroundColor: workspace.accent,
                height: 3,
              },
            }}
          >
            {items.map((item) => (
              <Tab
                key={item.path}
                value={item.path}
                label={
                  item.path === TODAY_PATH && today?.count ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {t(item.labelKey)}
                      <Chip
                        size="small"
                        label={today.count}
                        sx={{
                          height: 20,
                          minWidth: 20,
                          backgroundColor: workspace.accent,
                          color: "white",
                          fontWeight: 700,
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </Box>
                  ) : (
                    t(item.labelKey)
                  )
                }
                component={Link}
                to={item.path}
              />
            ))}
          </Tabs>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {renderContent()}
      </Container>
    </Box>
    </VoicesThemeProvider>
  );
};

export default VoicesLayout;
